// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { createTRPCClient, httpBatchLink, type TRPCClient } from "@trpc/client";
import { runInAction, when } from "mobx";
import moment from "moment";
import superjson from "superjson";

import type { AppRouter } from "~@sentencing/trpc-types";

import {
  FormAttributes,
  MutableSARAttributes,
} from "../components/CaseDetails/types";
import {
  FrequencyOfUse,
  MethodOfUse,
  SubstanceType,
} from "../components/OffenderAssessment/SubstanceUse/constants";
import { SentencingStore } from "../datastores/SentencingStore";
import { splitFullName } from "../utils/utils";

export type tRPCClient = TRPCClient<AppRouter>;

export type Staff = Awaited<ReturnType<APIClient["getStaffInfo"]>>;

export type Supervisor = Awaited<ReturnType<APIClient["getSupervisorInfo"]>>;

export type StaffCases = Staff["cases"];

export type StaffCase = StaffCases[number];

export type StaffSARs = Staff["sentencingAssessmentReports"];

export type StaffSAR = StaffSARs[number];

export type SARsByClient = Awaited<ReturnType<APIClient["getSARsByClient"]>>;

export type SARByClient = SARsByClient[number];

export type Case = Awaited<ReturnType<APIClient["getCaseDetails"]>>;

export type CaseInsight = NonNullable<Case["insight"]>;

export type Insight = Awaited<ReturnType<APIClient["getInsight"]>>;

export type SARInsight = Awaited<ReturnType<APIClient["getSARInsight"]>>;

export type SAR = Awaited<ReturnType<APIClient["getSARDetails"]>>;

export type Offenses = Awaited<ReturnType<APIClient["getOffenses"]>>;

export type Counties = Awaited<ReturnType<APIClient["getCounties"]>>;

export type Client = NonNullable<Case["client"]>;

export type Opportunities = Awaited<
  ReturnType<tRPCClient["opportunity"]["getOpportunities"]["query"]>
>;

export class APIClient {
  client?: tRPCClient;

  constructor(public readonly sentencingStore: SentencingStore) {
    when(
      () => !!this.sentencingStore.rootStore.userStore.getToken,
      async () => {
        const client = await this.initTRPCClient();
        runInAction(() => {
          this.client = client;
        });
      },
    );
  }

  get baseUrl(): string {
    return import.meta.env["VITE_SENTENCING_API_URL"];
  }

  get trpcClient() {
    return this.client;
  }

  async initTRPCClient(): Promise<tRPCClient | undefined> {
    if (!this.baseUrl) return;
    if (this.client) return this.client;

    const rootStore = this.sentencingStore.rootStore;

    return createTRPCClient<AppRouter>({
      links: [
        httpBatchLink({
          url: this.baseUrl,
          async headers() {
            if (!rootStore.userStore.getToken) return {};
            const token = await rootStore.userStore.getToken();
            const stateCode = rootStore.currentTenantId;

            return {
              Authorization: `Bearer ${token}`,
              StateCode: `${stateCode}`,
            };
          },
          // Required to get Date objects to serialize correctly.
          transformer: superjson,
        }),
      ],
    });
  }

  async getStaffInfo() {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    if (!this.sentencingStore.staffPseudoId)
      return Promise.reject({ message: "No staff pseudo id found" });

    const staffData = await this.trpcClient.staff.getStaff.query({
      pseudonymizedId: this.sentencingStore.staffPseudoId,
    });

    // Only fetch SARs if user has SAR access permission
    const sentencingAssessmentReports = this.sentencingStore.hasSARAccess
      ? await this.trpcClient.sar.getSARsForStaff.query({
          staffPseudonymizedId: this.sentencingStore.staffPseudoId,
        })
      : [];

    return {
      ...staffData,
      sentencingAssessmentReports: this.sentencingStore.activeFeatureVariants
        .PSRBuilder
        ? sentencingAssessmentReports
        : sentencingAssessmentReports.filter((sar) => sar.reportType === "SAR"),
    };
  }

  async getSupervisorInfo() {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    if (!this.sentencingStore.staffPseudoId)
      return Promise.reject({ message: "No staff pseudo id found" });

    const fetchedData = await this.trpcClient.supervisor.getSupervisor.query({
      pseudonymizedId: this.sentencingStore.staffPseudoId,
    });
    return fetchedData;
  }

  async setIsFirstLogin(pseudonymizedId: string) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return await this.trpcClient.staff.updateStaff.mutate({
      pseudonymizedId,
      hasLoggedIn: true,
    });
  }

  async getCaseDetails(caseId: string) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    const fetchedData = await this.trpcClient.case.getCase.query({
      id: caseId,
    });

    const { firstName, lastName } = splitFullName(fetchedData.client?.fullName);
    const updatedClient = fetchedData.client
      ? {
          ...fetchedData.client,
          firstName,
          lastName,
        }
      : undefined;

    return {
      ...fetchedData,
      age: moment().utc().diff(fetchedData.client?.birthDate, "years"),
      client: updatedClient,
    };
  }

  async getSARDetails(sarId: string) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    const fetchedData = await this.trpcClient.sar.getSAR.query({
      id: sarId,
    });

    const { firstName, lastName } = splitFullName(fetchedData.client?.fullName);
    const updatedClient = fetchedData.client
      ? {
          ...fetchedData.client,
          firstName,
          lastName,
        }
      : undefined;

    return {
      ...fetchedData,
      age: moment().utc().diff(fetchedData.client?.birthDate, "years"),
      client: updatedClient,
    };
  }

  async getSARsByClient(clientExternalId: string) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    const sars = await this.trpcClient.sar.getSARsByClient.query({
      clientExternalId,
    });

    return this.sentencingStore.activeFeatureVariants.PSRBuilder
      ? sars
      : sars.filter((sar) => sar.reportType === "SAR");
  }

  async updateCaseDetails(caseId: string, attributes: FormAttributes) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    return await this.trpcClient.case.updateCase.mutate({
      id: caseId,
      attributes,
    });
  }

  async updateSARDetails(
    SARId: string,
    attributes: Partial<MutableSARAttributes>,
  ) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    return await this.trpcClient.sar.updateSAR.mutate({
      id: SARId,
      attributes,
    });
  }

  async getCommunityOpportunities(isDemoMode = false): Promise<Opportunities> {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    const fetchedData =
      await this.trpcClient.opportunity.getOpportunities.query({
        includeFindHelpPrograms: isDemoMode,
      });

    return fetchedData;
  }

  async getOffenses() {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    const fetchedData = await this.trpcClient.offense.getOffenses.query();

    return fetchedData;
  }

  async getCounties() {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    const fetchedData = await this.trpcClient.county.getCounties.query();

    return fetchedData;
  }

  async getInsight(
    offense: string,
    gender: Client["gender"],
    lsirScore: number,
    isSexOffense?: boolean | null,
    isViolentOffense?: boolean | null,
  ) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    const fetchedData = await this.trpcClient.insight.getInsight.query({
      offenseName: offense,
      gender,
      lsirScore,
      isSexOffense,
      isViolentOffense,
    });

    return fetchedData;
  }

  async getSARInsight(
    offense: string,
    gender: Client["gender"],
    assessmentScoreBucket: number,
  ) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });

    const fetchedData = await this.trpcClient.sar.getSARInsight.query({
      offenseName: offense,
      gender,
      assessmentScoreBucket,
    });

    return fetchedData;
  }

  // Employment History CRUD methods
  async createEmploymentHistory(input: {
    sarId: string;
    employerName?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    verifiedByReportAuthor?: boolean | null;
  }) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return this.trpcClient.sar.createEmploymentHistory.mutate(input);
  }

  async updateEmploymentHistory(input: {
    id: string;
    employerName?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    verifiedByReportAuthor?: boolean | null;
  }) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return this.trpcClient.sar.updateEmploymentHistory.mutate(input);
  }

  async deleteEmploymentHistory(input: { id: string }) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return this.trpcClient.sar.deleteEmploymentHistory.mutate(input);
  }

  // Substance Use History CRUD methods

  async createDrugHistory(input: {
    sarId: string;
    substance?: SubstanceType | null;
    otherSubstanceName?: string | null;
    ageOfRegularUse?: number | null;
    admitsToCurrentUse?: boolean;
    lastUse?: Date | null;
    heaviestUse?: FrequencyOfUse | null;
    method?: MethodOfUse | null;
  }) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return this.trpcClient.sar.createDrugHistory.mutate(input);
  }

  async updateDrugHistory(input: {
    id: string;
    substance?: SubstanceType | null;
    otherSubstanceName?: string | null;
    ageOfRegularUse?: number | null;
    admitsToCurrentUse?: boolean;
    lastUse?: Date | null;
    heaviestUse?: FrequencyOfUse | null;
    method?: MethodOfUse | null;
  }) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return this.trpcClient.sar.updateDrugHistory.mutate(input);
  }

  async deleteDrugHistory(input: { id: string }) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return this.trpcClient.sar.deleteDrugHistory.mutate(input);
  }

  // Prior Treatment History CRUD methods
  async createPriorTreatmentHistory(input: {
    sarId: string;
    programName?: string | null;
    yearCompleted?: number | null;
    verifiedByReportAuthor?: boolean | null;
  }) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return this.trpcClient.sar.createPriorTreatmentHistory.mutate(input);
  }

  async updatePriorTreatmentHistory(input: {
    id: string;
    programName?: string | null;
    yearCompleted?: number | null;
    verifiedByReportAuthor?: boolean | null;
  }) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return this.trpcClient.sar.updatePriorTreatmentHistory.mutate(input);
  }

  async deletePriorTreatmentHistory(input: { id: string }) {
    if (!this.trpcClient)
      return Promise.reject({ message: "No tRPC client initialized" });
    return this.trpcClient.sar.deletePriorTreatmentHistory.mutate(input);
  }
}
